import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import type { Request } from 'express';

import getMatchedRoute from '@rover/react-app/src/server/renderPage/getMatchedRoute';
import { RoverLoading } from '@rover/react-lib/src/components';
import {
  apiCountryConfigurationNavigationRetrieve,
  getApiCountryConfigurationNavigationRetrieveQueryKey,
  useApiCountryConfigurationNavigationRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-country-configuration-navigation-retrieve';
import {
  apiFrontendConfigurationRetrieve,
  getApiFrontendConfigurationRetrieveQueryKey,
  useApiFrontendConfigurationRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import {
  apiFrontendServiceTypesList,
  getApiFrontendServiceTypesListQueryKey,
  useApiFrontendServiceTypesList,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-service-types-list';
import {
  apiSearchOptimizedRetrieve,
  getApiSearchOptimizedRetrieveQueryKey,
  useApiSearchOptimizedRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-search-optimized-retrieve';
import { EngineEnum } from '@rover/rsdk/src/apiClient/latest/model/engineEnum';
import { SeoLocation } from '@rover/rsdk/src/apiClient/latest/model/seoLocation';
import { captureMessage } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { getPath } from '@rover/shared/js/utils/getFullUrl';
import { Http500Error } from '@rover/utilities/HttpError';
import { filterNullishProperties } from '@rover/utilities/object';
import { parseSeoLocationFromTopLevelSlug } from '@rover/utilities/seo';
import { redirect } from '@rover/utilities/url.server';

import { handleSeoLocationQueryError } from './utils/handleSeoLocationQueryError';
import {
  getApiSearchSeoLocationFaqsListPrefetchQueryOptions,
  getApiSearchSeoLocationPrefetchQueryOptions,
  getApiSearchSeoLocationReviewsListPrefetchQueryOptions,
  useApiSearchSeoLocationRetrieve,
  useApiSearchSeoLocationReviewsList,
} from './utils/useSeoLocationHooks';
import SEO_LOCATION_PAGE_FOR_TYPE from './pages';
import SeoLocationPageTemplate from './SeoLocationPageTemplate';
import { getTopNeighborhoodServices } from './utils';

type RouteParams = {
  seoLocationSlug: string;
  countryCode?: string;
};

const SeoLocationPageContainer: FC & {
  reactQueryPrefetch: (queryClient: QueryClient, req: Request) => Promise<any>;
} = () => {
  const { seoLocationSlug = '', countryCode: countryUrlPrefix = 'us' } = useParams<RouteParams>();
  const seoLocationDescriptor = parseSeoLocationFromTopLevelSlug(seoLocationSlug, countryUrlPrefix);

  const {
    data: seoLocation,
    isError: isErrorSeoLocation,
    isLoading: isLoadingSeoLocation,
    error: errorSeoLocation,
  } = useApiSearchSeoLocationRetrieve(seoLocationDescriptor);
  if (!seoLocation) throw new Http500Error();
  const {
    isLoading: isLoadingReviews,
    isError: isErrorReviews,
    data: reviews,
  } = useApiSearchSeoLocationReviewsList(seoLocationDescriptor);

  const {
    isLoading: isLoadingCountryConfig,
    isError: isErrorCountryConfig,
    data: countryConfig,
  } = useApiCountryConfigurationNavigationRetrieve();
  const {
    isLoading: isLoadingFrontendConfig,
    isError: isErrorFrontendConfig,
    data: frontendConfig,
  } = useApiFrontendConfigurationRetrieve();
  const {
    isLoading: isLoadingServices,
    isError: isErrorServices,
    data: serviceTypes,
  } = useApiFrontendServiceTypesList();

  const { serviceTypeSlug } = seoLocation.details;
  const Page = SEO_LOCATION_PAGE_FOR_TYPE[serviceTypeSlug];
  const searchOptimizedRetrieveParams = filterNullishProperties(seoLocation.details.searchParams);
  const {
    isLoading: isLoadingSearch,
    isError: isErrorSearch,
    data: searchResponse,
    error: errorSearch,
  } = useApiSearchOptimizedRetrieve(searchOptimizedRetrieveParams);

  const isError =
    isErrorServices ||
    isErrorReviews ||
    isErrorCountryConfig ||
    isErrorFrontendConfig ||
    isErrorSeoLocation ||
    isErrorSearch;

  if (isError) {
    // 410s should be handled during prefetch, but just in case, also handle them here
    for (const error of [errorSeoLocation, errorSearch]) {
      if (error) {
        handleSeoLocationQueryError(error);
      }
    }

    captureMessage('Unhandled error loading SeoLocationPage', {
      tags: {
        pathname: getPath(),
        isErrorServices,
        isErrorReviews,
        isErrorCountryConfig,
        isErrorFrontendConfig,
        isErrorSeoLocation,
        isErrorSearch,
      },
    });
    throw new Http500Error();
  }

  const isLoading =
    isLoadingServices ||
    isLoadingReviews ||
    isLoadingFrontendConfig ||
    isLoadingCountryConfig ||
    isLoadingSeoLocation ||
    isLoadingSearch;
  if (isLoading) {
    if (process.env.JS_ENV_CLIENT) {
      // We shouldn't ever show a loading screen in the browser
      captureMessage(`SeoLocationPageContainer rendered with missing prefetches`, {
        tags: {
          pathname: getPath(),
          isLoadingServices,
          isLoadingReviews,
          isLoadingFrontendConfig,
          isLoadingCountryConfig,
          isLoadingSeoLocation,
          isLoadingSearch,
        },
      });
    }
    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  const topNeighborhoodServices = getTopNeighborhoodServices(seoLocation);
  const isTemplateEngine = seoLocation.details.configuration?.engine === EngineEnum.template;

  // DEV-144415 remove branching logic and always use template
  if (isTemplateEngine) {
    return (
      <SeoLocationPageTemplate
        seoLocation={seoLocation}
        seoLocationDescriptor={seoLocationDescriptor}
        reviews={reviews.results!}
        countryConfig={countryConfig}
        frontendConfig={frontendConfig}
        serviceTypes={serviceTypes}
        topNeighborhoodServices={topNeighborhoodServices}
        searchResponse={searchResponse}
      />
    );
  }

  return (
    <Page
      seoLocation={seoLocation}
      seoLocationDescriptor={seoLocationDescriptor}
      reviews={reviews.results!}
      countryConfig={countryConfig}
      frontendConfig={frontendConfig}
      serviceTypes={serviceTypes}
      topNeighborhoodServices={topNeighborhoodServices}
      searchResponse={searchResponse}
    />
  );
};

SeoLocationPageContainer.reactQueryPrefetch = async (queryClient: QueryClient, req: Request) => {
  const { pageRoute } = getMatchedRoute(req.path);
  // v6 `RouteMatch` flattens params onto the match itself — there's no nested
  // `.match` like there was in v5.
  const { seoLocationSlug, countryCode: countryUrlPrefix = 'us' } = pageRoute?.params ?? {};
  const seoLocationDescriptor = parseSeoLocationFromTopLevelSlug(seoLocationSlug, countryUrlPrefix);

  let seoLocation: SeoLocation;
  try {
    seoLocation = await queryClient.fetchQuery(
      getApiSearchSeoLocationPrefetchQueryOptions(seoLocationDescriptor)
    );
  } catch (error) {
    return handleSeoLocationQueryError(error);
  }

  // redirect if the canonical url doesn't match the requested url.
  // This can happen with typos (`---` instead of `--`), or if the service type slug
  // is not translated (`dog-boarding` instead of `cuidadores-de-perros` for /es/).
  const url = new URL(seoLocation.details.url);
  if (req.path !== url.pathname) {
    url.search = req.originalUrl.split('?')[1] ?? '';
    redirect(url);
  }

  const searchOptimizedRetrieveParams = filterNullishProperties(seoLocation.details.searchParams);

  const isTemplateEngine = seoLocation.details.configuration?.engine === EngineEnum.template;

  return Promise.all([
    queryClient
      .fetchQuery({
        queryKey: getApiSearchOptimizedRetrieveQueryKey(searchOptimizedRetrieveParams),
        queryFn: () => apiSearchOptimizedRetrieve(searchOptimizedRetrieveParams),
      })
      .catch(handleSeoLocationQueryError),
    queryClient.prefetchQuery({
      queryKey: getApiFrontendServiceTypesListQueryKey(),
      queryFn: () => apiFrontendServiceTypesList(),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiCountryConfigurationNavigationRetrieveQueryKey(),
      queryFn: () => apiCountryConfigurationNavigationRetrieve(),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiFrontendConfigurationRetrieveQueryKey(),
      queryFn: () => apiFrontendConfigurationRetrieve(),
    }),
    queryClient.prefetchQuery(
      getApiSearchSeoLocationReviewsListPrefetchQueryOptions(seoLocationDescriptor)
    ),
    // Only the template engine renders the FAQs list, so skip the prefetch otherwise.
    ...(isTemplateEngine
      ? [
          queryClient.prefetchQuery(
            getApiSearchSeoLocationFaqsListPrefetchQueryOptions(
              seoLocationDescriptor,
              seoLocation.localityType
            )
          ),
        ]
      : []),
  ]);
};

export default SeoLocationPageContainer;
